// mostly copied from https://github.com/mrdoob/three.js/blob/dev/examples/jsm/loaders/PLYLoader.js
import ExternalLoader, {
  GeometryRawData,
  LoaderDataType, Material,
  ObjectData
} from "_src/engine/loaders/external/external_loader";
import output from "_src/engine/output";
import config from "_src/config";
import {messageType} from "_src/engine/ipc";
import workerManager from "_src/engine/worker_manager";
import plyCommon, {Buffer} from "_src/engine/loaders/external/ply/ply_common";
import {StreamProxy} from "_src/engine/loaders/external/utils";

const bufferSizeLimit = 1850000000 / 4; //1.8gb / 4bytes per float ot uint
export default class PlyLoader extends ExternalLoader {
  constructor() {
    super();
    this._dataType = LoaderDataType.STREAM;

    this.propertyNameMapping = {};
    this.header = null;
    this.headerTailDataChunk = null;
  }

  async _parse(reader, progress = null) {
    async function parseHeader() {
      const decoder = new TextDecoder();
      let currentDataChunk = await streamProxy.getChunk(false);
      if (!currentDataChunk) {
        throw new Error( "ExternalLoader.PlyLoader: Ply file is empty");
      }

      let currentTextChunk = decoder.decode(currentDataChunk, {stream: true});
      if (!currentTextChunk.startsWith("ply")) {
        throw new Error( "ExternalLoader.PlyLoader: Ply file doesn't start with ply keyword");
      }

      const header = {
        comments: [],
        elements: [],
        objInfo: ""
      };
      let currentElement;

      function make_ply_element_property( propertValues, propertyNameMapping ) {

        const property = { type: propertValues[ 0 ] };

        if ( property.type === "list" ) {

          property.name = propertValues[ 3 ];
          property.countType = propertValues[ 1 ];
          property.itemType = propertValues[ 2 ];

        } else {

          property.name = propertValues[ 1 ];

        }

        if ( property.name in propertyNameMapping ) {

          property.name = propertyNameMapping[ property.name ];

        }

        return property;

      }

      let truncatedLine = "";
      for (;;) {
        const lines = currentTextChunk.split( /\r\n|\r|\n/ );
        lines[0] = truncatedLine + lines[0];
        const lastLine = lines[lines.length - 1];
        const isLastLineFull = lastLine.endsWith("\r\n") || lastLine.endsWith("\r") || lastLine.endsWith("\n");
        const tailDrop = isLastLineFull ? 0 : 1;
        truncatedLine = isLastLineFull ? "" : lastLine;

        let foundEndHeader = false;
        for ( let i = 0; i < lines.length - tailDrop; i ++ ) {

          let line = lines[ i ];
          line = line.trim();

          if ( line === "" ) continue;

          const lineValues = line.split( /\s+/ );
          const lineType = lineValues.shift();
          line = lineValues.join( " " );

          switch ( lineType ) {

            case "format":

              header.format = lineValues[ 0 ];
              header.version = lineValues[ 1 ];

              break;

            case "comment":

              header.comments.push( line );

              break;

            case "element":

              if ( currentElement !== undefined ) {

                header.elements.push( currentElement );

              }

              currentElement = {};
              currentElement.name = lineValues[ 0 ];
              currentElement.count = parseInt( lineValues[ 1 ] );
              currentElement.properties = [];

              break;

            case "property":

              currentElement.properties.push( make_ply_element_property( lineValues, scope.propertyNameMapping ) );

              break;

            case "obj_info":

              header.objInfo = line;

              break;

            case "end_header":
              foundEndHeader = true;
              break;

            case "ply":
              break;

            default:

              output.warn( "ExternalLoader.PLYLoader: unhandled", lineType, lineValues );

          }

          if (foundEndHeader) break;
        }

        if (foundEndHeader) {
          const patternHeader = /^([\s\S]*)end_header(\r\n|\r|\n)/;
          const result = patternHeader.exec(currentTextChunk);
          const droppedDataChunk = currentDataChunk.slice(result[0].length);
          if (droppedDataChunk.byteLength) {
            scope.headerTailDataChunk = droppedDataChunk;
          }

          break;
        }

        currentDataChunk = await streamProxy.getChunk(false);
        if (!currentDataChunk) {
          throw new Error( "ExternalLoader.PlyLoader: Ply header doesn't end");
        }
        currentTextChunk = decoder.decode(currentDataChunk, {stream: true});
      }

      if ( currentElement !== undefined ) {
        header.elements.push( currentElement );
      }

      return header;
    }

    class TypedArrayBuffer extends Buffer {
      constructor(header) {
        super();

        const verticesDesc = header.elements.find(desc => desc.name === "vertex");
        const verticesTypeDesc = verticesDesc.properties.find(component => component.name === "x");

        const normalsDesc = header.elements.find(desc => desc.name === "vertex");
        const normalsTypeDesc = normalsDesc.properties.find(component => component.name === "nx");

        const uvsDesc = header.elements.find(desc => desc.name === "vertex");
        const uvsTypeDesc = uvsDesc.properties.find(component => component.name === "s");

        if (Math.max(verticesDesc.count * 3, normalsDesc.count * 3, uvsDesc.count * 2) > bufferSizeLimit) {
          throw new Error( "ExternalLoader.PlyLoader: Model is too big");
        }

        const verticesBuffer = verticesDesc && verticesTypeDesc ? new (plyCommon.arrayConstructorByType(verticesTypeDesc.type))(verticesDesc.count * 3) : null;
        const normalsBuffer = normalsDesc && normalsTypeDesc ? new (plyCommon.arrayConstructorByType(normalsTypeDesc.type))(normalsDesc.count * 3) : null;
        const uvsBuffer = uvsDesc && uvsTypeDesc ? new (plyCommon.arrayConstructorByType(uvsTypeDesc.type))(uvsDesc.count * 2) : null;
        this.store = [];
        this.store[this.VERTICES] = verticesBuffer;
        this.store[this.INDICES] = null;
        this.store[this.NORMALS] = normalsBuffer;
        this.store[this.UVS] = uvsBuffer;

        this.pointers = [];
        this.pointers[this.VERTICES] = 0;
        this.pointers[this.INDICES] = 0;
        this.pointers[this.NORMALS] = 0;
        this.pointers[this.UVS] = 0;

        this.header = header;
        this.currentElementsByFaceInitialized = 0;
      }

      checkOrInitIndices() {
        if (this.store[this.INDICES] && this.currentElementsByFaceInitialized === this.elementsByFace) return;

        const indicesDesc = this.header.elements.find(desc => desc.name === "face");
        const indicesTypeDesc = indicesDesc.properties.find(component => component.name === "vertex_indices");

        if (indicesDesc.count * this.elementsByFace > bufferSizeLimit) {
          throw new Error( "ExternalLoader.PlyLoader: Model is too big");
        }
        const indicesBuffer = indicesDesc && indicesTypeDesc ? new (plyCommon.arrayConstructorByType(indicesTypeDesc.itemType))(indicesDesc.count * this.elementsByFace) : null;

        if (this.store[this.INDICES]) {
          indicesBuffer.set(this.store[this.INDICES]);
        }
        this.store[this.INDICES] = indicesBuffer;
        this.currentElementsByFaceInitialized = this.elementsByFace;
      }

      push(bufferType, value) {
        this.store[bufferType][this.pointers[bufferType]++] = value;
      }
    }

    async function parseASCII(buffer) {
      // PLY ascii format specification, as per http://en.wikipedia.org/wiki/PLY_(file_format)
      let dataChunk = scope.headerTailDataChunk ? scope.headerTailDataChunk : await streamProxy.getChunk();
      if (!dataChunk) {
        throw new Error( "ExternalLoader.PlyLoader: No data after header");
      }

      for (;;) {
        const parsePromise = parseChunk(dataChunk, 0, false, buffer);
        const loadPromise = streamProxy.getChunk();

        dataChunk = (await Promise.all([loadPromise, parsePromise]))[0];
        if (!dataChunk) break;
      }

      return postProcess( buffer );
    }

    async function parseBinary(buffer) {
      let currentChunk;
      let nextChunk;
      let dataView;
      let dataSize;

      const buildDataView = () => {
        let mergedChunk = null;
        if (currentChunk && nextChunk) {
          const totalLength = currentChunk.byteLength + nextChunk.byteLength;
          mergedChunk = new Uint8Array(totalLength);
          mergedChunk.set(currentChunk, 0);
          mergedChunk.set(nextChunk, currentChunk.byteLength);
        } else if (currentChunk) {
          mergedChunk = currentChunk;
        }
        dataView = mergedChunk ? new DataView(mergedChunk.buffer) : null;
        dataSize = currentChunk ? currentChunk.byteLength : 0;
      }
      const advanceChunks = async () => {
        [currentChunk, nextChunk] = await streamProxy.getTwoChunks();
      }

      await advanceChunks();
      if (scope.headerTailDataChunk) {
        if (currentChunk) {
          const mergedChunk = new Uint8Array(scope.headerTailDataChunk.length + currentChunk.length);
          mergedChunk.set(scope.headerTailDataChunk);
          mergedChunk.set(currentChunk, scope.headerTailDataChunk.byteLength);
          currentChunk = mergedChunk;
        } else {
          currentChunk = scope.headerTailDataChunk;
        }
      }
      buildDataView();

      if (!dataView) {
        throw new Error( "ExternalLoader.PlyLoader: No data after header");
      }
      for (;;) {
        const parsePromise = parseChunk(dataView, dataSize, true, buffer);
        const loadPromise = advanceChunks();

        await Promise.all([loadPromise, parsePromise]);
        buildDataView();
        if (!dataView) break;
      }

      return postProcess( buffer );

    }

    function postProcess( buffer ) {
      const geometryData = new GeometryRawData(buffer.store[buffer.VERTICES]);
      geometryData.indices = buffer.store[buffer.INDICES];
      geometryData.normals = buffer.store[buffer.NORMALS];
      geometryData.uvs = buffer.store[buffer.UVS];
      const materialData = new Material();
      const objectData = new ObjectData(geometryData, materialData);

      return objectData;
    }

    async function initParser() {
      config.useWorkers ? await initParserWorker() : await initParserMain();
    }

    async function initParserWorker() {
      const message = {
        type : messageType.INIT_PLY,
        header : scope.header,
      };
      const tid = workerManager.generateUniqueID();
      await workerManager.sendMessage(message, [], tid);
    }

    async function initParserMain() {
      plyCommon.init(scope.header);
    }

    async function parseChunk(dataChunk, dataSize, isBinary, buffer) {
      config.useWorkers ? await parseChunkWorker(dataChunk, dataSize, isBinary, buffer) : await parseChunkMain(dataChunk, dataSize, isBinary, buffer);
    }

    async function parseChunkMain(dataChunk, dataSize, isBinary, buffer) {
      isBinary ? plyCommon.decodeBinary(dataChunk, dataSize, buffer) : plyCommon.decodeASCII(dataChunk, buffer);
    }

    async function parseChunkWorker(dataChunk, dataSize, isBinary, buffer) {
      const message = {
        type : messageType.PARSE_PLY,
        dataChunk : dataChunk,
        dataSize : dataSize,
        isBinary : isBinary,
      };
      const tid = workerManager.generateUniqueID();
      const transfer = [dataChunk.buffer];

      const workerData = (await workerManager.sendMessage(message, transfer, tid)).data;
      if (workerData.vertices) {
        buffer.store[buffer.VERTICES].set(workerData.vertices, buffer.pointers[buffer.VERTICES]);
        buffer.pointers[buffer.VERTICES] += workerData.vertices.length;
      }

      if (workerData.indices) {
        buffer.updateElementsByFace(workerData.elementsByFace);
        buffer.checkOrInitIndices();
        buffer.store[buffer.INDICES].set(workerData.indices, buffer.pointers[buffer.INDICES]);
        buffer.pointers[buffer.INDICES] += workerData.indices.length;
      }

      if (workerData.normals) {
        buffer.store[buffer.NORMALS].set(workerData.normals, buffer.pointers[buffer.NORMALS]);
        buffer.pointers[buffer.NORMALS] += workerData.normals.length;
      }

      if (workerData.uvs) {
        buffer.store[buffer.UVS].set(workerData.uvs, buffer.pointers[buffer.UVS]);
        buffer.pointers[buffer.UVS] += workerData.uvs.length;
      }

    }

    //

    const scope = this;
    const streamProxy = new StreamProxy(reader, progress);
    streamProxy.start();
    scope.header = await parseHeader();
    await initParser();
    const buffer = new TypedArrayBuffer(scope.header);

    const objectData = scope.header.format === "ascii" ? await parseASCII(buffer) : await parseBinary(buffer);

    if (progress) progress.emit(progress.LOADING, 1);
    return [objectData];
  }
}
